import React from "react";
import { OpenInNew } from "@mui/icons-material";
import { Box, Button, Chip, Divider, Grid, Stack, Typography } from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { formatPercentFromFraction } from "utils/formatters/PercentFormatters";
import { PricingVector, type RateCard, type RateCardUnitPrice } from "client/api-v2";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { detailFieldLabelSx, DetailField } from "components/InfoField/InfoField";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";

const allVectors = Object.values(PricingVector);

function formatMoney(amount: number): string {
  return amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Monospace({ children }: { children: React.ReactNode }) {
  return (
    <Box component="span" sx={{ fontFamily: "monospace" }}>
      {children}
    </Box>
  );
}

function UnitPricesTable({ unitPrices }: { unitPrices: RateCardUnitPrice[] }) {
  const priceByVector = new Map(unitPrices.map((p) => [p.vector, p.listPricePerUnit]));

  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ color: "text.secondary", ...detailFieldLabelSx }}>
        Unit prices
      </Typography>
      <Stack sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
        {allVectors.map((vector, index) => {
          const price = priceByVector.get(vector);
          return (
            <React.Fragment key={vector}>
              <Stack direction="row" sx={{ justifyContent: "space-between", px: 2, py: 1 }}>
                <Typography variant="body1" sx={{ color: price === undefined ? "text.secondary" : "text.primary" }}>
                  {vector}
                </Typography>
                <Typography variant="body1" sx={{ color: price === undefined ? "text.secondary" : "text.primary" }}>
                  {price === undefined ? "not priced" : <Monospace>${formatMoney(price)}</Monospace>}
                </Typography>
              </Stack>
              {index < allVectors.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </Stack>
    </Stack>
  );
}

export function RateCardDetails() {
  const { entity: rateCard } = useEntity<RateCard>();

  if (!rateCard) {
    return null;
  }

  return (
    <DetailsPageLayout
      pageName={rateCard.label || rateCard.code}
      actions={[
        <Button
          key="assigned-subscriptions"
          variant="contained"
          component="a"
          target="_blank"
          href={`${subscriptionRoutes.root}?rateCardCode=${rateCard.code}`}
          rel="noopener noreferrer"
        >
          Assigned Subscriptions <OpenInNew fontSize="small" sx={{ paddingLeft: 1 }} />
        </Button>,
      ]}
    >
      <Chip label="Immutable" size="small" variant="outlined" sx={{ mb: 3 }} />
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <UnitPricesTable unitPrices={rateCard.unitPrices} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Stack spacing={2}>
            <DetailField label="Currency" value={rateCard.currency} />
            <DetailField label="Discount exponent (V)" value={<Monospace>{rateCard.discountExponent}</Monospace>} />
            <DetailField
              label="Discount threshold"
              value={`${rateCard.discountThresholdUnits} units — discount applies above this quantity`}
            />
            <DetailField
              label="Overall discount"
              value={
                rateCard.overallDiscountFraction === 0
                  ? "No overall discount"
                  : `${formatPercentFromFraction(
                      rateCard.overallDiscountFraction
                    )} off the total, before the round-down`
              }
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Stack spacing={2}>
            <DetailField
              label="Fixed fees"
              value={
                rateCard.fixedFees.length === 0 ? (
                  "None"
                ) : (
                  <Stack spacing={0.5}>
                    {rateCard.fixedFees.map((fee, index) => (
                      <Typography key={`${fee.description}-${index}`} variant="body1">
                        {fee.description}: <Monospace>${formatMoney(fee.amount)}</Monospace>
                      </Typography>
                    ))}
                  </Stack>
                )
              }
            />
            <DetailField
              label="Final-price round-down"
              value={
                rateCard.finalPriceRoundDownToNearest === null ||
                rateCard.finalPriceRoundDownToNearest === undefined ? (
                  "None"
                ) : (
                  <Monospace>${formatMoney(rateCard.finalPriceRoundDownToNearest)}</Monospace>
                )
              }
            />
            <DetailField
              label="Created"
              value={`${formatDateOnly(getOrParseDateTime(rateCard.createdDate))} by ${rateCard.createdByUser}`}
            />
          </Stack>
        </Grid>
      </Grid>
    </DetailsPageLayout>
  );
}
